/* ============================================================
   useRelationContext — relation/person/rollup 속성이 필요로 하는
   부가 데이터(조직 멤버, 대상 DB 행, 행 제목, DB 목록)를 배치 로드하는 훅
   - N+1 방지: 대상 DB 단위로 1회 batch fetch 후 캐시 (targets)
   - 행 제목: 현재 rows 의 relation 값에 등장하는 id 를 모아 1회 조회 (titleById)
   - members: workspace.members (org_id 기준) — useMembers.js 와 동일 패턴, 읽기 전용
   ============================================================ */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { relationIds, UUID_RE } from './computeValue';

const ws = () => supabase.schema('workspace');

export function useRelationContext(page, columns, rows) {
  const orgId = page?.org_id ?? null;

  const [members, setMembers] = useState([]);
  const [databases, setDatabases] = useState([]); // org 내 type='database' 페이지 (relation 대상 선택용)
  const [titleMap, setTitleMap] = useState(() => new Map()); // rowPageId → { title, icon }
  const [targets, setTargets] = useState({}); // dbId → { loading, error, columns, rows, rowsById }

  const fetchingTargets = useRef(new Set());
  const fetchedTitleIds = useRef(new Set());

  /* ---- 조직 멤버 (person 선택기) ---- */
  useEffect(() => {
    let active = true;
    if (!orgId) { setMembers([]); return () => { active = false; }; }
    (async () => {
      const { data } = await ws()
        .from('members')
        .select('id, user_id, display_name, avatar_url')
        .eq('org_id', orgId);
      if (active) setMembers(data || []);
    })();
    return () => { active = false; };
  }, [orgId]);

  /* ---- org 내 데이터베이스 목록 (relation 컬럼의 대상 DB 선택) ---- */
  useEffect(() => {
    let active = true;
    if (!orgId) { setDatabases([]); return () => { active = false; }; }
    (async () => {
      const { data } = await ws()
        .from('pages')
        .select('id, title, icon')
        .eq('org_id', orgId)
        .eq('type', 'database');
      if (active) setDatabases(data || []);
    })();
    return () => { active = false; };
  }, [orgId]);

  /* ---- relation 값에 등장하는 행 페이지 제목 batch 조회 (칩 표시용) ---- */
  useEffect(() => {
    const relCols = (columns || []).filter((c) => c.type === 'relation');
    if (!relCols.length || !rows?.length) return undefined;
    const want = new Set();
    rows.forEach((r) => {
      relCols.forEach((c) => {
        relationIds(r.properties?.[c.key]).forEach((id) => {
          if (UUID_RE.test(String(id)) && !fetchedTitleIds.current.has(id)) want.add(id);
        });
      });
    });
    if (!want.size) return undefined;
    const ids = [...want];
    ids.forEach((id) => fetchedTitleIds.current.add(id));
    let active = true;
    (async () => {
      const { data } = await ws().from('pages').select('id, title, icon').in('id', ids);
      if (!active) return;
      setTitleMap((prev) => {
        const next = new Map(prev);
        (data || []).forEach((p) => next.set(p.id, { title: p.title, icon: p.icon }));
        return next;
      });
      // 조회했지만 결과에 없는 id(삭제/RLS 접근 불가)는 map 미등록 → 칩에서 "(접근 불가)" 폴백
    })();
    return () => { active = false; };
  }, [columns, rows]);

  /* ---- 대상 DB 데이터 로드 (rollup 집계 / relation 편집 검색) ----
     DB 단위 1회 fetch 후 캐시. 중복 호출은 ref 로 차단. */
  const ensureTarget = useCallback((dbId) => {
    if (!dbId || !UUID_RE.test(String(dbId))) return;
    if (fetchingTargets.current.has(dbId)) return;
    fetchingTargets.current.add(dbId);
    setTargets((prev) => ({
      ...prev,
      [dbId]: { loading: true, error: null, columns: [], rows: [], rowsById: {} },
    }));
    (async () => {
      try {
        const { data: sch } = await ws()
          .from('db_schemas')
          .select('columns')
          .eq('database_id', dbId)
          .maybeSingle();
        const tcols = sch && Array.isArray(sch.columns) ? sch.columns : [];

        const { data: rrows, error: rErr } = await ws()
          .from('db_rows')
          .select('page_id, properties')
          .eq('database_id', dbId);
        if (rErr) throw rErr;

        const ids = (rrows || []).map((r) => r.page_id);
        const titles = {};
        if (ids.length) {
          const { data: rps } = await ws().from('pages').select('id, title, icon').in('id', ids);
          (rps || []).forEach((p) => { titles[p.id] = p; });
        }
        const trows = (rrows || []).map((r) => ({
          page_id: r.page_id,
          properties: r.properties || {},
          title: titles[r.page_id]?.title || '',
          icon: titles[r.page_id]?.icon || '📄',
        }));
        const rowsById = {};
        trows.forEach((t) => { rowsById[t.page_id] = t; });
        setTargets((prev) => ({
          ...prev,
          [dbId]: { loading: false, error: null, columns: tcols, rows: trows, rowsById },
        }));
      } catch (e) {
        setTargets((prev) => ({
          ...prev,
          [dbId]: { loading: false, error: e.message || String(e), columns: [], rows: [], rowsById: {} },
        }));
      }
    })();
  }, []);

  /* rollup 이 참조하는 relation 대상 DB 는 렌더에 필요하므로 미리 로드 */
  useEffect(() => {
    (columns || []).forEach((c) => {
      if (c.type !== 'rollup') return;
      const rel = (columns || []).find((x) => x.key === c.rollup?.relationKey && x.type === 'relation');
      if (rel?.relationDb) ensureTarget(rel.relationDb);
    });
  }, [columns, ensureTarget]);

  const peopleById = useMemo(() => {
    const m = new Map();
    members.forEach((mm) => { if (mm.user_id) m.set(mm.user_id, mm); });
    return m;
  }, [members]);

  return useMemo(
    () => ({ members, peopleById, databases, titleById: titleMap, targets, ensureTarget }),
    [members, peopleById, databases, titleMap, targets, ensureTarget]
  );
}
